import { styled } from "goober";
import { useMemo } from "react";
import {
  defaultChatModel,
  defaultFastModel,
} from "../../../shared/aiProviders";
import { Box } from "../Box";
import { Text } from "../Text";
import type { AIConfigDraft } from "./aiProviderDraft";
import {
  AiProviderSpendLimitsSection,
  type LimitsDraft,
} from "./AiProviderSpendLimitsSection";
import {
  FormCard,
  PriceGrid,
  PriceRow,
  ReadOnlyCell,
  SectionHeading,
  UsdInput,
} from "./modalForm";

export type PricingDraft = Record<string, { input: string; output: string }>;

const Sections = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

type Props = {
  draft: AIConfigDraft;
  providerId: string;
  pricingRows: ReadonlyArray<{
    aiProviderId: string;
    modelId: string;
    inputPerMillion: number;
    outputPerMillion: number;
  }>;
  pricingDraft: PricingDraft;
  limitsDraft: LimitsDraft;
  disabled: boolean;
  limitsDisabled: boolean;
  onPricingChange: (next: PricingDraft) => void;
  onLimitsChange: (next: LimitsDraft) => void;
};

export function AiProviderPricingTab({
  draft,
  providerId,
  pricingRows,
  pricingDraft,
  limitsDraft,
  disabled,
  limitsDisabled,
  onPricingChange,
  onLimitsChange,
}: Props) {
  const modelIds = useMemo(() => {
    const ids = new Set<string>();
    const chat = draft.chatModel.trim() || defaultChatModel(draft.provider);
    const fast = draft.fastModel.trim() || defaultFastModel(draft.provider);
    if (chat) ids.add(chat);
    if (fast) ids.add(fast);
    for (const row of pricingRows) {
      if (row.aiProviderId === providerId) ids.add(row.modelId);
    }
    return [...ids];
  }, [draft, providerId, pricingRows]);

  const baselineFor = (modelId: string) => {
    const row = pricingRows.find(
      (row) => row.aiProviderId === providerId && row.modelId === modelId
    );
    return {
      input: row == null ? "" : String(row.inputPerMillion),
      output: row == null ? "" : String(row.outputPerMillion),
    };
  };

  const displayed = (modelId: string) => {
    const baseline = baselineFor(modelId);
    const drafted = pricingDraft[modelId];
    return {
      input: drafted?.input ?? baseline.input,
      output: drafted?.output ?? baseline.output,
    };
  };

  const setField = (
    modelId: string,
    field: "input" | "output",
    value: string
  ) => {
    onPricingChange({
      ...pricingDraft,
      [modelId]: { ...displayed(modelId), [field]: value },
    });
  };

  return (
    <Sections>
      <FormCard style={{ gap: 24 }}>
        <SectionHeading
          title="Model pricing"
          description="Set the price per million tokens for each model used by this provider."
        />
        {modelIds.length === 0 ? (
          <Box horizontal={8}>
            <Text variant="text-small" color="var(--text-secondary)">
              Add a chat model to configure pricing.
            </Text>
          </Box>
        ) : (
          <PriceGrid>
            <PriceRow>
              <Box left={8}>
                <Text variant="text-small-md">Model</Text>
              </Box>
              <Box left={8}>
                <Text variant="text-small-md">Input ($/1M tokens)</Text>
              </Box>
              <Box left={8}>
                <Text variant="text-small-md">Output ($/1M tokens)</Text>
              </Box>
            </PriceRow>
            {modelIds.map((modelId) => {
              const value = displayed(modelId);
              return (
                <PriceRow key={modelId}>
                  <ReadOnlyCell>{modelId}</ReadOnlyCell>
                  <UsdInput
                    value={value.input}
                    placeholder="0.00"
                    disabled={disabled}
                    onChange={(next) => setField(modelId, "input", next)}
                  />
                  <UsdInput
                    value={value.output}
                    placeholder="0.00"
                    disabled={disabled}
                    onChange={(next) => setField(modelId, "output", next)}
                  />
                </PriceRow>
              );
            })}
          </PriceGrid>
        )}
      </FormCard>

      <AiProviderSpendLimitsSection
        providerId={providerId}
        limitsDraft={limitsDraft}
        disabled={limitsDisabled}
        onChange={onLimitsChange}
      />
    </Sections>
  );
}
